// 數學模板數據 - Mathematics Template Data
export const mathTemplateTypes = [
  {
    id: 'proof',
    name: '數學證明',
    description: '用於證明數學定理或命題',
    icon: '🧮',
    color: 'bg-blue-500'
  },
  {
    id: 'solution',
    name: '解題步驟',
    description: '用於解決數學問題的步驟',
    icon: '✏️',
    color: 'bg-green-500'
  },
  {
    id: 'analysis',
    name: '數學分析',
    description: '用於分析數學概念或函數',
    icon: '📊',
    color: 'bg-purple-500'
  },
  {
    id: 'calculation',
    name: '計算過程',
    description: '用於展示計算步驟',
    icon: '🔢',
    color: 'bg-red-500'
  }
];

export const mathTemplates = [
  {
    id: 'proof-template-1',
    type: 'proof',
    title: '幾何證明模板',
    category: 'geometry',
    description: '用於證明幾何定理的標準格式',
    structure: {
      given: '已知條件',
      toProve: '求證',
      proof: '證明過程',
      conclusion: '結論'
    },
    content: {
      given: '已知：在三角形ABC中，AB = AC',
      toProve: '求證：∠B = ∠C',
      proof: `證明：
1. 作AD⊥BC，D為垂足
2. 在直角三角形ABD和ACD中：
   - AB = AC（已知）
   - AD = AD（公共邊）
   - ∠ADB = ∠ADC = 90°（作圖）
3. 因此 △ABD ≅ △ACD（RHS）
4. 所以 ∠B = ∠C（對應角相等）`,
      conclusion: '因此，等腰三角形的兩個底角相等。'
    },
    formulas: [
      '\\triangle ABD \\cong \\triangle ACD',
      '\\angle B = \\angle C'
    ]
  },
  {
    id: 'solution-template-1',
    type: 'solution',
    title: '二次方程解法模板',
    category: 'algebra',
    description: '用於解二次方程的標準步驟',
    structure: {
      problem: '問題',
      method: '解法',
      steps: '解題步驟',
      verification: '驗證',
      answer: '答案'
    },
    content: {
      problem: '解方程：$x^2 - 5x + 6 = 0$',
      method: '因式分解法',
      steps: `解題步驟：
1. 觀察方程：$x^2 - 5x + 6 = 0$
2. 尋找兩個數，使其乘積為6，和為-5
3. 這兩個數是-2和-3
4. 因此：$(x - 2)(x - 3) = 0$
5. 所以：$x - 2 = 0$ 或 $x - 3 = 0$
6. 解得：$x = 2$ 或 $x = 3$`,
      verification: `驗證：
當 $x = 2$ 時：$2^2 - 5(2) + 6 = 4 - 10 + 6 = 0$ ✓
當 $x = 3$ 時：$3^2 - 5(3) + 6 = 9 - 15 + 6 = 0$ ✓`,
      answer: '因此，方程的解為 $x = 2$ 或 $x = 3$'
    },
    formulas: [
      'x^2 - 5x + 6 = 0',
      '(x - 2)(x - 3) = 0',
      'x = 2 \\text{ 或 } x = 3'
    ]
  },
  {
    id: 'analysis-template-1',
    type: 'analysis',
    title: '函數分析模板',
    category: 'calculus',
    description: '用於分析函數性質的標準格式',
    structure: {
      function: '函數',
      domain: '定義域',
      range: '值域',
      properties: '性質分析',
      graph: '圖像特點',
      conclusion: '總結'
    },
    content: {
      function: '分析函數：$f(x) = x^2 - 4x + 3$',
      domain: '定義域：$\\mathbb{R}$（所有實數）',
      range: '值域：$[-1, +\\infty)$',
      properties: `性質分析：
1. 這是一個二次函數，開口向上
2. 對稱軸：$x = -\\frac{b}{2a} = -\\frac{-4}{2(1)} = 2$
3. 頂點：$(2, f(2)) = (2, -1)$
4. 與x軸的交點：解 $x^2 - 4x + 3 = 0$，得 $x = 1$ 或 $x = 3$
5. 與y軸的交點：$f(0) = 3$，即點$(0, 3)$`,
      graph: `圖像特點：
- 拋物線開口向上
- 最低點為頂點$(2, -1)$
- 在$x < 2$時遞減，在$x > 2$時遞增
- 與坐標軸有三個交點：$(0, 3)$、$(1, 0)$、$(3, 0)$`,
      conclusion: '這是一個標準的二次函數，具有典型的拋物線性質。'
    },
    formulas: [
      'f(x) = x^2 - 4x + 3',
      'x = \\frac{4 \\pm \\sqrt{16 - 12}}{2} = \\frac{4 \\pm 2}{2}',
      '\\text{頂點：}(2, -1)'
    ]
  },
  {
    id: 'calculation-template-1',
    type: 'calculation',
    title: '三角函數計算模板',
    category: 'trigonometry',
    description: '用於三角函數計算的標準步驟',
    structure: {
      problem: '計算題目',
      given: '已知條件',
      formula: '使用公式',
      calculation: '計算過程',
      result: '最終結果'
    },
    content: {
      problem: '計算：$\\sin(75°)$',
      given: '已知：$75° = 45° + 30°$',
      formula: '使用和角公式：$\\sin(A + B) = \\sin A \\cos B + \\cos A \\sin B$',
      calculation: `計算過程：
$\\sin(75°) = \\sin(45° + 30°)$
$= \\sin 45° \\cos 30° + \\cos 45° \\sin 30°$
$= \\frac{\\sqrt{2}}{2} \\cdot \\frac{\\sqrt{3}}{2} + \\frac{\\sqrt{2}}{2} \\cdot \\frac{1}{2}$
$= \\frac{\\sqrt{6}}{4} + \\frac{\\sqrt{2}}{4}$
$= \\frac{\\sqrt{6} + \\sqrt{2}}{4}$`,
      result: '因此，$\\sin(75°) = \\frac{\\sqrt{6} + \\sqrt{2}}{4}$'
    },
    formulas: [
      '\\sin(A + B) = \\sin A \\cos B + \\cos A \\sin B',
      '\\sin(75°) = \\frac{\\sqrt{6} + \\sqrt{2}}{4}'
    ]
  }
];

export const mathUsefulExpressions = {
  proof: [
    { id: 'proof-1', text: '已知', latex: '\\text{已知：}' },
    { id: 'proof-2', text: '求證', latex: '\\text{求證：}' },
    { id: 'proof-3', text: '證明', latex: '\\text{證明：}' },
    { id: 'proof-4', text: '因為', latex: '\\because' },
    { id: 'proof-5', text: '所以', latex: '\\therefore' },
    { id: 'proof-6', text: '顯然', latex: '\\text{顯然}' },
    { id: 'proof-7', text: '由此可得', latex: '\\text{由此可得}' },
    { id: 'proof-8', text: '證畢', latex: '\\text{證畢}' }
  ],
  solution: [
    { id: 'solution-1', text: '解', latex: '\\text{解：}' },
    { id: 'solution-2', text: '設', latex: '\\text{設}' },
    { id: 'solution-3', text: '代入', latex: '\\text{代入}' },
    { id: 'solution-4', text: '化簡', latex: '\\text{化簡}' },
    { id: 'solution-5', text: '整理得', latex: '\\text{整理得}' },
    { id: 'solution-6', text: '解得', latex: '\\text{解得}' },
    { id: 'solution-7', text: '驗證', latex: '\\text{驗證}' },
    { id: 'solution-8', text: '答案', latex: '\\text{答案：}' }
  ],
  analysis: [
    { id: 'analysis-1', text: '分析', latex: '\\text{分析：}' },
    { id: 'analysis-2', text: '討論', latex: '\\text{討論：}' },
    { id: 'analysis-3', text: '當...時', latex: '\\text{當...時}' },
    { id: 'analysis-4', text: '情況一', latex: '\\text{情況一：}' },
    { id: 'analysis-5', text: '情況二', latex: '\\text{情況二：}' },
    { id: 'analysis-6', text: '綜合以上', latex: '\\text{綜合以上}' },
    { id: 'analysis-7', text: '總結', latex: '\\text{總結：}' }
  ],
  calculation: [
    { id: 'calculation-1', text: '計算', latex: '\\text{計算：}' },
    { id: 'calculation-2', text: '步驟', latex: '\\text{步驟}' },
    { id: 'calculation-3', text: '原式', latex: '\\text{原式}' },
    { id: 'calculation-4', text: '等於', latex: '=' },
    { id: 'calculation-5', text: '約等於', latex: '\\approx' },
    { id: 'calculation-6', text: '大於', latex: '>' },
    { id: 'calculation-7', text: '小於', latex: '<' },
    { id: 'calculation-8', text: '結果', latex: '\\text{結果：}' }
  ]
};

export default mathTemplates; 